<template>
  <div class="language-switcher" ref="switcher">
    <div v-if="selectedLanguage" class="selected-language" @click="toggleDropdown">
      <span class="language-name">{{ selectedLanguage.nativeName }}</span>
      <i class="pi pi-chevron-down"></i>
    </div>
    <div v-if="isOpen" class="language-dropdown">
      <div 
        v-for="lang in availableLanguages" 
        :key="lang.code" 
        class="language-option" 
        @click="changeLanguage(lang.code)"
      >
        <span class="language-name">{{ lang.nativeName }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import { setDocumentDirection } from '@/i18n'

export default {
  name: 'LanguageSwitcher',
  setup() {
    const { locale, availableLocales: i18nAvailableLocales, messages } = useI18n()
    const isOpen = ref(false)
    const switcher = ref(null)

    const availableLanguages = computed(() => {
        return i18nAvailableLocales.map(code => ({
            code,
            name: messages.value[code]?.languageName || code,
            nativeName: messages.value[code]?.nativeName || code
        }))
    })
    
    const selectedLanguage = computed(() => {
        return availableLanguages.value.find(lang => lang.code === locale.value) || availableLanguages.value[0]
    })

    const changeLanguage = (newLocale) => {
      if (newLocale && newLocale !== locale.value) {
        locale.value = newLocale
      }
      isOpen.value = false
    }

    watch(locale, async (newLocale, oldLocale) => {
      if (newLocale && newLocale !== oldLocale) {
        localStorage.setItem('user-locale', newLocale)
        document.querySelector('html').setAttribute('lang', newLocale)

        // Set the document direction using the centralized function
        setDocumentDirection(newLocale);

        try {
          const token = localStorage.getItem('access_token')
          if (token) {
            await api.setUserLanguage(newLocale)
          }
        } catch (error) {
          console.warn('Failed to update backend language preference:', error)
        }
      }
    })

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value
    }

    const closeDropdown = (event) => {
      if (switcher.value && !switcher.value.contains(event.target)) {
        isOpen.value = false
      }
    }

    onMounted(() => {
      document.addEventListener('click', closeDropdown)
    })

    onUnmounted(() => {
      document.removeEventListener('click', closeDropdown)
    })

    return {
      selectedLanguage,
      availableLanguages,
      changeLanguage,
      isOpen,
      switcher,
      toggleDropdown
    }
  }
}
</script>

<style lang="scss" scoped>
.language-switcher {
  position: relative;
}
.selected-language {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: var(--secondary-color);
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--primary-color);
    color: white;
  }
}
.language-dropdown {
  position: absolute;
  top: 110%;
  right: 0;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 153, 255, 0.15);
  border: 1px solid var(--border-color);
  overflow: hidden;
  z-index: 10;
}
.language-option {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--secondary-color);
    color: var(--primary-color);
  }
}

.language-name {
  font-size: 0.9em;
}

/* RTL support */
[dir="rtl"] .language-option {
  flex-direction: row-reverse;
}

/* Responsive design */
@media (max-width: 768px) {
  .language-name {
    font-size: 0.8em;
  }
}
</style> 